import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Paperclip, Send, Square, X } from "lucide-react";
import { useIsMutating } from "@tanstack/react-query";
import { AronModelPicker } from "./AronModelPicker";
import { AronAccessPicker } from "./AronAccessPicker";
import { AronMissionVerdict } from "./AronMissionVerdict";
import {
  ARON_SET_ACCESS_KEY,
  useAronPersonaUi,
  useAronConversationInfo,
  useAronMessages,
  useAronUsage,
  useAronWorkModes,
} from "../api/aronApi";
import { useAronUiStore } from "../store/aronUiStore";
import { useAronYolo } from "./aronYolo";
import {
  attachmentProblem,
  formatLimits,
  inlineTotalProblem,
  limitsTitle,
} from "../utils/attachmentLimits";

const ACCEPT =
  "image/png,image/jpeg,image/gif,image/webp,application/pdf,.dxf,.dwg,.docx,.odt,.xlsx," +
  ".txt,.log,.md,.csv,.tsv,.json,.xml,.html,.htm,.zip";
/** Gdy backend jeszcze nie odpowiedział limitami trybu — ta sama liczba, co domyślne
 *  ARON_MAX_ATTACHMENTS; prawdziwy sufit przychodzi w work_modes → limits. */
const DEFAULT_MAX_FILES = 20;

// Co backend realnie przyjmuje — apps/aron/services/attachments.py
// (ALLOWED_ATTACHMENT_TYPES + _EXT_TO_TYPE + _GENERIC_TYPES). Trzymamy to tutaj,
// bo inaczej plik odpada dopiero przy wysyłce, jednym błędem 400: użytkownik
// widział dołączony plik, a wątek kończył się bez odpowiedzi.
const DXF_TYPE = "application/dxf";
// DWG (binarny AutoCAD) — backend konwertuje go do DXF przy zapisie załącznika.
const DWG_TYPE = "application/dwg";
// Dokumenty biurowe — backend wyciąga z nich tekst u siebie i podaje modelowi jako
// treść (nie jako obraz), a po sam plik sięga narzędzie warsztatu (workshop_fetch).
const DOCX_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ODT_TYPE = "application/vnd.oasis.opendocument.text";
// Archiwum ZIP — backend rozpakowuje je u siebie, a pliki ze środka stają się
// osobnymi załącznikami wiadomości (paczka na hasło czeka na hasło od człowieka).
const ZIP_TYPE = "application/zip";
const XLSX_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
// Pliki, które SAME są tekstem — backend odczytuje je u siebie i podaje modelowi jako
// treść, dokładnie tak jak dokumenty biurowe. Musi być zgodne z PLAIN_TEXT_TYPES.
const TEXT_TYPES = [
  "text/plain",
  "text/html",
  "text/markdown",
  "text/csv",
  "text/tab-separated-values",
  "text/xml",
  "application/xml",
  "application/json",
];
const ALLOWED_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  DXF_TYPE,
  DWG_TYPE,
  DOCX_TYPE,
  ODT_TYPE,
  XLSX_TYPE,
  ZIP_TYPE,
  ...TEXT_TYPES,
]);
const EXT_TO_TYPE: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  dxf: DXF_TYPE,
  dwg: DWG_TYPE,
  docx: DOCX_TYPE,
  odt: ODT_TYPE,
  xlsx: XLSX_TYPE,
  txt: "text/plain",
  log: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  json: "application/json",
  xml: "text/xml",
  html: "text/html",
  htm: "text/html",
  zip: ZIP_TYPE,
};
/** Rozszerzenia rozstrzygające same z siebie — DXF i DWG przychodzą z przeglądarki
 *  pod różnymi nazwami („image/vnd.dxf", „application/acad", „text/plain", nic),
 *  a .docx/.xlsx bywają zadeklarowane jako stary format binarny („application/msword",
 *  „application/vnd.ms-excel"). Musi być zgodne z _EXT_WINS w attachments.py. */
const EXT_WINS: Record<string, string> = {
  dxf: DXF_TYPE,
  dwg: DWG_TYPE,
  docx: DOCX_TYPE,
  odt: ODT_TYPE,
  xlsx: XLSX_TYPE,
  // .csv Windows deklaruje jako „application/vnd.ms-excel", .md/.log bywają
  // octet-stream — rozszerzenie pliku tekstowego jest jednoznaczne, więc orzeka.
  txt: "text/plain",
  log: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  json: "application/json",
  xml: "text/xml",
  html: "text/html",
  htm: "text/html",
  // Windows/Outlook deklaruje ZIP jako „application/x-zip-compressed" — deklaracja
  // KONKRETNA, więc bez tej reguły plik poszedłby dalej pod typem, którego backend
  // nie zna. Musi być zgodne z _EXT_WINS w attachments.py.
  zip: ZIP_TYPE,
};
/** „Nie wiem, co to za plik" w wykonaniu przeglądarki i Windowsa — tylko wtedy
 *  o typie decyduje rozszerzenie (dokładnie jak po stronie serwera). */
const GENERIC_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
  "application/download",
  "application/force-download",
  "application/unknown",
  "content/unknown",
]);
const UNSUPPORTED_HINT =
  "ARON czyta obrazy (PNG/JPEG/GIF/WebP), PDF, DXF, DWG, dokumenty DOCX, ODT i XLSX, " +
  "pliki tekstowe (TXT, MD, LOG, CSV, TSV, JSON, XML, HTML) oraz archiwa ZIP.";

function effectiveType(f: File): string {
  const declared = (f.type || "").toLowerCase().trim();
  const parts = f.name.toLowerCase().split(".");
  const ext = parts.length > 1 ? parts[parts.length - 1] : "";
  if (ext && EXT_WINS[ext]) return EXT_WINS[ext];
  if (!GENERIC_TYPES.has(declared)) return declared;
  return ext ? (EXT_TO_TYPE[ext] ?? declared) : declared;
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

/** Ile kosztowała TA rozmowa — jedna liczba w rogu, żeby nie trzeba było
 *  sumować kosztów spod pojedynczych odpowiedzi.
 *
 *  Bierzemy większą z dwóch wartości: sumy kosztów wczytanych wiadomości (rośnie
 *  natychmiast po odpowiedzi) i serwerowego podsumowania rozmowy (obejmuje też
 *  tury, których wiadomości nie ma już w widoku). Większa z nich nigdy nie
 *  zaniża rachunku. */
function ConversationCost({ conversationId }: { conversationId?: string | null }) {
  const conv = useAronConversationInfo(conversationId ?? null);
  const { data: messages = [] } = useAronMessages(conversationId ?? null);
  if (!conversationId) return null;

  const fromMessages = messages.reduce((sum, m) => {
    const n = Number(m.cost_usd);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const rollup = Number(conv?.total_cost_usd);
  const total = Math.max(fromMessages, Number.isFinite(rollup) ? rollup : 0);
  if (total <= 0) return null;

  return (
    <span
      className="shrink-0 font-mono text-[10px] text-gray-400"
      title="Koszt całej tej rozmowy (tokeny wszystkich tur ARON-a)"
    >
      {`$${total >= 0.01 ? total.toFixed(2) : total.toFixed(4)}`}
    </span>
  );
}

/** Kwota dziennego limitu — dwa miejsca po przecinku, po polsku, zawsze z „$". */
function fmtUsd(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2).replace(".", ",")}`;
}

/** Dzienny limit ARON-a: PROCENT I KWOTA obok siebie.
 *
 *  Sam procent nikomu nic nie mówił — „24%" nie odpowiada na pytanie „24% czego i za
 *  ile?" (zgłoszenie „ARON – limit procentowy"). Limit jest kwotowy, więc kwota jest
 *  tu liczbą główną, a procent — miarą, jak blisko ściany jesteśmy. Pasek narzędzi
 *  jest wąski (na telefonie dzieli linię z modelem i dostępem), więc w rogu stoi kwota
 *  WYDANA i procent, a cały rachunek — wydane / limit / reszta / tokeny — w podpowiedzi. */
function BudgetIndicator() {
  const { data } = useAronUsage();
  if (!data) return null;
  const spent = parseFloat(data.cost_today_usd) || 0;
  const cap = parseFloat(data.daily_cost_budget_usd) || 0;
  const pct = cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 0;
  const danger = pct >= 90;
  return (
    <span
      className={`shrink-0 font-mono text-[10px] ${danger ? "text-red-600" : "text-gray-400"}`}
      title={
        `Dzienny limit ARON: ${fmtUsd(spent)} z ${fmtUsd(cap)} (${pct}%). ` +
        `Zostało ${fmtUsd(data.remaining_usd)}. ` +
        `Tokeny dziś: ${fmtTokens(data.tokens_today)}.`
      }
    >
      {fmtUsd(spent)} · {pct}%
    </span>
  );
}

interface Props {
  onSend: (text: string, files?: File[], opts?: { compressImages?: boolean }) => void;
  busy: boolean;
  onStop: () => void;
  /** Tura trwa, a rozmowa przyjmuje DOPISKI: pole zostaje czynne obok Stop i wysyłka
   *  dokłada tekst do trwającej odpowiedzi (ARON czyta go na granicy kroku, więc
   *  „zauważy" nie znaczy „przerwie w pół zdania"). Bez tej flagi busy = tylko Stop. */
  addendumAllowed?: boolean;
  /** The open conversation's id. When set, its model/access are FROZEN — the pickers
   * render locked, showing that conversation's actual values. Null/undefined = new
   * conversation → interactive pickers driving the next conversation's defaults. */
  conversationId?: string | null;
}

/** Co widok rozmowy może zrobić z kompozytorem — dokładanie plików upuszczonych
 *  gdziekolwiek nad wątkiem trafia tą drogą do tej samej listy, którą zapełnia
 *  spinacz. */
export interface AronComposerHandle {
  addFiles: (files: File[]) => void;
  /** Model tej rozmowy w ogóle przyjmuje pliki (vision) — strefa upuszczania
   *  ma się nie odzywać tam, gdzie spinacz jest schowany. */
  attachAllowed: boolean;
}

const ACCESS_SWITCHING_NOTE =
  "Zmiana trybu dostępu jeszcze się zapisuje — wyślij za chwilę, inaczej wiadomość pójdzie w poprzednim trybie.";

export const AronComposer = forwardRef<AronComposerHandle, Props>(function AronComposer(
  { onSend, busy, onStop, addendumAllowed = false, conversationId }: Props,
  ref,
) {
  // Ten sam komponent obsługuje trzy persony (ARON, ENG-ARON, asystent PDF) —
  // teksty biorą się z persony, nie z porównania adresu.
  const persona = useAronPersonaUi();
  const assistantName =
    persona === "eng" ? "ENG-ARON" : persona === "pdf" ? "asystent PDF" : "ARON";
  // Krótko: pole i tak stoi pod rozmową, a skrót Enter/Shift+Enter jest w dymku
  // przycisku „Wyślij" — placeholder ma zachęcać do pisania, nie tłumaczyć obsługę.
  const promptPlaceholder =
    persona === "eng"
      ? "Co sprawdzić w dokumentacji?"
      : persona === "pdf"
        ? "Co zrobić z tymi plikami?"
        : "Co mam sprawdzić albo zrobić?";
  const [value, setValue] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  // Plik odrzucony jeszcze przed wysyłką — powód pokazujemy nad polem pisania.
  const [rejected, setRejected] = useState<string | null>(null);
  // Mobile only — tapping the compact field opens a full-screen editor so there's
  // room to write (the inline 2-row box is cramped on a phone).
  const [expanded, setExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const conv = useAronConversationInfo(conversationId ?? null);
  const { data: workModes } = useAronWorkModes();
  const locked = !!conversationId;
  const modelMode = useAronUiStore((s) => s.modelMode);
  const compressImages = useAronUiStore((s) => s.compressImagesDefault);
  const setCompressImages = useAronUiStore((s) => s.setCompressImagesDefault);
  const yolo = useAronYolo();
  // Active model: a locked conversation uses its frozen mode; a new one, the picker.
  const activeMode = locked ? conv?.reasoning_mode : modelMode;
  // Gate attach + paste on the model's vision capability — the backend is the
  // single source of truth (mode_config.vision), so any text-only model (DeepSeek,
  // DeepSeek Pro, …) is handled without hardcoding names. Default to off until the
  // modes load so we never offer attach for a model that would reject it.
  const activeModeCfg = workModes?.reasoning_modes.find((m) => m.value === activeMode);
  const attachAllowed = !!activeModeCfg?.vision;
  // Limity załączników TEGO modelu — liczby dostawcy z backendu; kompozytor tylko je
  // powtarza i sprawdza przed wysyłką, żeby odmowa nie przychodziła dopiero jako 400.
  const limits = activeModeCfg?.limits;
  const maxFiles = limits?.max_files ?? DEFAULT_MAX_FILES;

  // The field grows upward with the text instead of scrolling inside two rows —
  // a długie polecenie widać w całości przed wysłaniem. The CSS max-height caps
  // it (and only then a scrollbar appears); sending resets it, because `value`
  // going back to "" re-runs this.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  // Image previews for the selected-file chips — created once per file set, revoked on change.
  const previews = useMemo(
    () => files.map((f) => (f.type.startsWith("image/") ? URL.createObjectURL(f) : "")),
    [files],
  );
  useEffect(
    () => () => previews.forEach((u) => u && URL.revokeObjectURL(u)),
    [previews],
  );

  // Opcja kompresji ma sens tylko przy obrazach — a jej cena (rozmyty drobny tekst
  // na zrzucie ekranu) jest realna, więc pole wyboru pokazujemy dopiero wtedy, gdy
  // jest co kompresować, i piszemy przy nim, ile te obrazy ważą.
  const imageFiles = files.filter((f) => effectiveType(f).startsWith("image/"));
  const hasImages = imageFiles.length > 0;
  const imageMb = imageFiles.reduce((sum, f) => sum + f.size, 0) / 1_000_000;

  // Przełączenie Odczyt ↔ Zapis tej rozmowy jeszcze leci do serwera. Tura czyta tryb z
  // bazy w chwili startu, więc wiadomość wysłana teraz poszłaby w STARYM trybie, choć
  // przycisk (optymistycznie) pokazuje już nowy (META-ARON b101478d). Dopisek do
  // trwającej tury to nie dotyczy — ona i tak ma tryb ustalony na starcie.
  const accessSwitching =
    useIsMutating({
      mutationKey: ARON_SET_ACCESS_KEY,
      predicate: (m) =>
        (m.state.variables as { conversationId?: string } | undefined)
          ?.conversationId === conversationId,
    }) > 0;
  useEffect(() => {
    if (!accessSwitching)
      setRejected((r) => (r === ACCESS_SWITCHING_NOTE ? null : r));
  }, [accessSwitching]);

  // Dopisek do trwającej odpowiedzi: sam tekst, bez plików (backend odrzuca je 400 —
  // załącznik ma iść po zakończeniu tury, gdzie model może go naprawdę obejrzeć).
  const canAddendum = busy && addendumAllowed;
  const submit = () => {
    const t = value.trim();
    if ((!t && files.length === 0) || (busy && !canAddendum)) return;
    if (accessSwitching && !canAddendum) {
      setRejected(ACCESS_SWITCHING_NOTE);
      return;
    }
    if (canAddendum && files.length > 0) {
      setRejected(
        "Do trwającej odpowiedzi można dopisać tylko tekst — załączniki wyślij po jej zakończeniu.",
      );
      return;
    }
    // Kompresja dotyczy TEJ wysyłki i tylko obrazów — przy samym tekście albo PDF-ie
    // nie ma czego kompresować, więc nie zaśmiecamy nią żądania.
    const compress = compressImages && hasImages;
    // Ostatnia bramka przed wysyłką, tymi samymi liczbami co backend: plik ponad limit
    // modelu i suma obrazów+PDF-ów ponad jedno zapytanie. Odmowa tutaj jest tańsza niż
    // 400 po wgraniu — i mówi, co zrobić (kompresja, podział, TWO Drive).
    const described = files.map((f) => ({ name: f.name, ctype: effectiveType(f), size: f.size }));
    const blocker =
      described
        .map((f) => attachmentProblem(f.name, f.ctype, f.size, limits, compress))
        .find((problem) => problem !== null) ?? inlineTotalProblem(described, limits, compress);
    if (blocker) {
      setRejected(blocker);
      return;
    }
    onSend(t, files, { compressImages: compress });
    setValue("");
    setFiles([]);
    setRejected(null);
    setExpanded(false);
  };

  // Jedno wejście dla wszystkich trzech dróg (spinacz, wklejenie, upuszczenie),
  // żeby limit, filtr typów i kolejność chipów działały tak samo w każdej z nich.
  const addFiles = (picked: File[]) => {
    if (picked.length === 0) return;
    const ok: File[] = [];
    const notes: string[] = [];
    const unsupported: string[] = [];
    let needCompression = false;
    for (const f of picked) {
      const ctype = effectiveType(f);
      if (!ALLOWED_TYPES.has(ctype)) {
        unsupported.push(f.name);
        continue;
      }
      const problem = attachmentProblem(f.name, ctype, f.size, limits, compressImages);
      if (problem) {
        // Obraz ponad limit dostawcy, który ZMIEŚCI SIĘ po przeskalowaniu: nie odrzucamy,
        // tylko włączamy kompresję i mówimy o tym — pole wyboru pojawia się dopiero
        // przy obrazach, więc bez tego użytkownik nie miałby jak jej zaznaczyć.
        if (ctype.startsWith("image/") && attachmentProblem(f.name, ctype, f.size, limits, true) === null) {
          needCompression = true;
          ok.push(f);
          continue;
        }
        notes.push(`Pominięto: ${problem}`);
        continue;
      }
      ok.push(f);
    }
    if (unsupported.length > 0) {
      notes.unshift(`Pominięto: ${unsupported.join(", ")} — ${UNSUPPORTED_HINT}`);
    }
    const room = Math.max(0, maxFiles - files.length);
    if (ok.length > room) {
      notes.push(
        `Ten model przyjmuje najwyżej ${maxFiles} plików na wiadomość — dalsze pominięto.`,
      );
    }
    if (needCompression && !compressImages) {
      setCompressImages(true);
      notes.push(
        `Obraz przekracza limit modelu (${limits ? formatLimits(limits).split(" · ")[1] : "obraz"}) — włączono „Kompresuj obrazy”.`,
      );
    }
    setRejected(notes.length > 0 ? notes.join(" ") : null);
    if (ok.length > 0) setFiles((prev) => [...prev, ...ok].slice(0, maxFiles));
  };

  // Upuszczenie pliku nad rozmową (AronChatView łapie zdarzenie i woła to tutaj)
  // ma dokładać plik dokładnie tak samo jak „Przeglądaj" — bez własnego filtra
  // typów, bo o dopuszczalnym typie i tak orzeka backend przy wysyłce.
  useImperativeHandle(ref, () => ({ addFiles, attachAllowed }), [attachAllowed]);

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = ""; // allow re-picking the same file
  };

  // Paste an image straight from the clipboard (Ctrl+V a screenshot) — same flow
  // as the paperclip. Plain-text pastes fall through untouched.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!attachAllowed) return;
    const imgs: File[] = [];
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const f = item.getAsFile();
        if (!f) continue;
        const ext = item.type.split("/")[1] || "png";
        // Pasted screenshots all arrive as a generic "image.png" — give each a unique name.
        imgs.push(new File([f], `wklejony-${Date.now()}-${imgs.length}.${ext}`, { type: item.type }));
      }
    }
    if (imgs.length === 0) return; // no image in clipboard → let the text paste happen
    e.preventDefault();
    addFiles(imgs);
  };

  const canSend = !!value.trim() || files.length > 0;

  const fileChips = files.length > 0 && (
    <div className="flex flex-wrap gap-1.5">
      {files.map((f, i) => (
        <span
          key={`${f.name}-${i}`}
          className="inline-flex max-w-[12rem] items-center gap-1 border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          title={f.name}
        >
          {previews[i] ? (
            <img src={previews[i]} alt="" className="h-4 w-4 rounded object-cover" />
          ) : (
            <Paperclip size={12} />
          )}
          <span className="truncate">{f.name}</span>
          <button
            type="button"
            onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
            className="shrink-0 text-gray-400 hover:text-red-600"
            aria-label="Usuń plik"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      {limits && (
        <span
          className="basis-full text-[10px] text-gray-400"
          title={limitsTitle(limits)}
          data-testid="composer-limits"
        >
          limity modelu: {formatLimits(limits)}
        </span>
      )}
    </div>
  );

  /** Wybór na JEDNO zapytanie: obrazy w oryginale (domyślnie) albo przeskalowane do
   *  1600 px JPEG. Kompresja mieści w limicie zdjęcie z terenu i skan, ale potrafi
   *  rozmyć drobny tekst na zrzucie ekranu — dlatego decyduje człowiek, nie serwer. */
  const compressToggle = hasImages && (
    <label
      className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"
      title={
        "Obrazy zostaną przeskalowane do 1600 px (JPEG) przed wysłaniem do modelu. " +
        "Włącz przy zdjęciach z terenu i skanach — dzięki temu mieszczą się w limicie " +
        "jednego zapytania. Zostaw wyłączone, gdy model ma odczytać drobny tekst " +
        "(zrzut ekranu, tabela) — kompresja może go rozmyć."
      }
    >
      <input
        type="checkbox"
        checked={compressImages}
        onChange={(e) => setCompressImages(e.target.checked)}
        className="h-3.5 w-3.5 accent-red-600"
      />
      <span>
        Kompresuj obrazy{" "}
        <span className="text-gray-400">
          ({imageFiles.length}{" "}
          {imageFiles.length === 1 ? "plik" : imageFiles.length < 5 ? "pliki" : "plików"},{" "}
          {imageMb.toFixed(1)} MB)
        </span>
      </span>
    </label>
  );

  const rejectedNotice = rejected && (
    <div className="flex items-start gap-1.5 border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
      <span className="min-w-0 flex-1">{rejected}</span>
      <button
        type="button"
        onClick={() => setRejected(null)}
        className="shrink-0 text-red-400 hover:text-red-600"
        aria-label="Ukryj komunikat"
      >
        <X size={12} />
      </button>
    </div>
  );

  const attachButton = attachAllowed && (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={busy || files.length >= maxFiles}
      title={
        limits
          ? `Załącz pliki — ${formatLimits(limits)}`
          : `Załącz obraz, PDF lub rysunek DXF/DWG (max ${maxFiles})`
      }
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-gray-300 text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
    >
      <Paperclip size={15} />
    </button>
  );

  const placeholder = canAddendum
    ? "Dopisz do trwającej odpowiedzi — ARON uwzględni to przy najbliższym kroku"
    : promptPlaceholder;

  const sendButton = (
    <button
      type="button"
      onClick={submit}
      disabled={!canSend}
      title={
        canAddendum
          ? "Dopisz do trwającej odpowiedzi (Enter; Shift+Enter = nowa linia)"
          : "Wyślij (Enter; Shift+Enter = nowa linia)"
      }
      aria-label={canAddendum ? "Dopisz do trwającej odpowiedzi" : "Wyślij"}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center text-white transition-colors disabled:opacity-40 ${
        yolo ? "bg-black hover:bg-gray-800" : "bg-red-600 hover:bg-red-700"
      }`}
    >
      <Send size={15} />
    </button>
  );
  const stopButton = (
    <button
      type="button"
      onClick={onStop}
      title="Przerwij odpowiedź"
      aria-label="Przerwij odpowiedź"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
    >
      <Square size={14} />
    </button>
  );
  // Tura trwa: Stop jak dotąd; przy dopiskach obok niego zostaje też Wyślij — dopisanie
  // jednego słowa nie może kosztować zabicia całej tury i zapłacenia za nią od nowa.
  const sendOrStop = busy ? (
    canAddendum ? (
      <span className="inline-flex shrink-0 items-center gap-1">
        {sendButton}
        {stopButton}
      </span>
    ) : (
      stopButton
    )
  ) : (
    sendButton
  );

  return (
    <>
      <div className="border-t border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
       <div className="mx-auto w-full max-w-4xl">
        {/* One quiet toolbar row: model, access, budget — the choices themselves
            open in modals, so a narrow split pane never has to carry them. */}
        <div className="mb-1.5 flex items-center gap-1.5 overflow-hidden">
          <AronModelPicker
            locked={locked}
            lockedValue={conv?.reasoning_mode}
            conversationId={conversationId}
            busy={busy}
          />
          <AronAccessPicker conversationId={conversationId} value={conv?.access_level} autopilot={conv?.autopilot} unlimitedSteps={conv?.unlimited_steps} />
          <AronMissionVerdict conversationId={conversationId} value={conv?.mission_success} />
          {/* Prawy róg paska: najpierw rachunek za tę rozmowę, potem dzienny limit. */}
          <span className="ml-auto flex shrink-0 items-center gap-2">
            <ConversationCost conversationId={conversationId} />
            <BudgetIndicator />
          </span>
        </div>

        {/* Hidden file input — shared by the desktop and modal attach buttons. */}
        {attachAllowed && (
          <input ref={fileInputRef} type="file" accept={ACCEPT} multiple hidden onChange={onPickFiles} />
        )}

        {rejectedNotice && <div className="mb-1.5 sm:mb-2">{rejectedNotice}</div>}
        {files.length > 0 && (
          <div className="mb-1.5 space-y-1.5 sm:mb-2">
            {fileChips}
            {compressToggle}
          </div>
        )}

        {/* Desktop — inline textarea with keyboard hints. */}
        <div className="hidden items-end gap-2 sm:flex">
          {attachButton}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            onPaste={handlePaste}
            rows={2}
            placeholder={placeholder}
            className={`max-h-[45vh] min-h-[56px] flex-1 resize-none overflow-y-auto border bg-white px-2.5 py-1.5 text-sm focus:outline-none dark:bg-gray-800 ${
              yolo
                ? "border-black focus:border-black dark:border-white"
                : "border-gray-300 focus:border-red-500 dark:border-gray-600"
            }`}
          />
          {sendOrStop}
        </div>

        {/* Mobile — compact single-line field; tap to open the full-screen editor. */}
        <div className="flex items-center gap-2 sm:hidden">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex min-w-0 flex-1 items-center border border-gray-300 bg-white px-3 py-2.5 text-left text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <span className={`truncate ${value ? "" : "text-gray-400"}`}>
              {value || placeholder}
            </span>
          </button>
          {sendOrStop}
        </div>
       </div>
      </div>

      {/* Mobile full-screen editor */}
      {expanded && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white sm:hidden dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700">
            <span className="text-sm font-medium">Wiadomość: {assistantName}</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Zamknij"
            >
              <X size={20} />
            </button>
          </div>
          <textarea
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onPaste={handlePaste}
            placeholder={`Napisz wiadomość: ${assistantName}…`}
            className="min-h-0 flex-1 resize-none px-4 py-3 text-base focus:outline-none dark:bg-gray-900"
          />
          {rejectedNotice && <div className="px-4 pb-2">{rejectedNotice}</div>}
          {files.length > 0 && (
            <div className="space-y-1.5 px-4 pb-2">
              {fileChips}
              {compressToggle}
            </div>
          )}
          <div className="flex items-center gap-2 border-t border-gray-200 p-3 dark:border-gray-700">
            {attachButton}
            <div className="ml-auto">{sendOrStop}</div>
          </div>
        </div>
      )}
    </>
  );
});
